import React, { useState, useEffect } from 'react';
import DiagramViewer from './DiagramViewer';

const DiagramAnalysisTool = () => {
  const [questionText, setQuestionText] = useState('');
  const [topic, setTopic] = useState('Geometry');
  const [options, setOptions] = useState(['', '', '', '']);
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  const sampleQuestions = {
    triangle: "A right triangle has sides of length 3 cm, 4 cm, and 5 cm. What is the area of this triangle?",
    rectangle: "A rectangle has a length of 8 meters and a width of 5 meters. What is the perimeter?",
    circle: "A circle has a radius of 6 centimeters. What is the circumference?",
    fractions: "On a number line from 0 to 10, mark the position of 2½ and 7½.",
    data: "In a survey, 15 students chose pizza, 10 chose burgers, and 8 chose salad. Which food was most popular?"
  };

  const handleAnalyze = async () => {
    if (!questionText.trim()) {
      setError('Please enter a question to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysis(null);
    setDebugInfo({
      backendUrl: process.env.REACT_APP_BACKEND_URL,
      fullApiUrl: `${process.env.REACT_APP_BACKEND_URL}/api/questions/analyze-diagram`,
      timestamp: new Date().toISOString(),
      questionText,
      topic,
      envVarCheck: {
        NODE_ENV: process.env.NODE_ENV,
        allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      }
    });

    try {
      console.log('🔍 Making diagram analysis request:', {
        url: `${process.env.REACT_APP_BACKEND_URL}/api/questions/analyze-diagram`,
        questionText,
        topic,
        options: options.filter(opt => opt.trim())
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/questions/analyze-diagram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question_text: questionText,
          topic: topic,
          options: options.filter(opt => opt.trim())
        })
      });

      console.log('🔍 Response status:', response.status, response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 Diagram analysis result:', result);
        console.log('🔍 needs_diagram value:', result.needs_diagram, typeof result.needs_diagram);
        setAnalysis(result);
      } else {
        const errorData = await response.json();
        console.log('❌ Analysis error response:', errorData);
        setError(errorData.detail || 'Failed to analyze question');
      }
    } catch (err) {
      console.log('❌ Network/parsing error:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadSampleQuestion = (type) => {
    setQuestionText(sampleQuestions[type]);
    setTopic(type === 'triangle' || type === 'rectangle' || type === 'circle' ? 'Geometry' : 
           type === 'fractions' ? 'Fractions' : 'Statistics');
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  return (
    <div className="diagram-analysis-tool p-6 max-w-6xl mx-auto">
      <div className="header mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Diagram Analysis Tool</h1>
        <p className="text-gray-600">
          Test the automatic diagram detection system. Enter a math question and see if the AI recommends a visual diagram.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Panel - Input */}
        <div className="input-panel">
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Question Input</h2>
            
            {/* Sample Questions */}
            <div className="sample-questions mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Try a sample question:</p>
              <div className="flex flex-wrap gap-2">
                {Object.keys(sampleQuestions).map((type) => (
                  <button
                    key={type}
                    onClick={() => loadSampleQuestion(type)}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 capitalize"
                  >
                    {type.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Question Text */}
            <div className="form-group mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Text
              </label>
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                className="w-full h-24 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter a math question to analyze..."
              />
            </div>

            {/* Topic */}
            <div className="form-group mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topic
              </label>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="Geometry">Geometry</option>
                <option value="Algebra">Algebra</option>
                <option value="Fractions">Fractions</option>
                <option value="Statistics">Statistics</option>
                <option value="Measurement">Measurement</option>
                <option value="Number">Number</option>
              </select>
            </div>

            {/* Options */}
            <div className="form-group mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Multiple Choice Options (Optional)
              </label>
              {options.map((option, index) => (
                <input
                  key={index}
                  type="text"
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  className="w-full p-2 mb-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder={`Option ${index + 1}`}
                />
              ))}
            </div>

            {/* Analyze Button */}
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing || !questionText.trim()}
              className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAnalyzing ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Analyzing Question...
                </span>
              ) : (
                'Analyze for Diagrams'
              )}
            </button>
          </div>
        </div>

        {/* Right Panel - Results */}
        <div className="results-panel">
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Analysis Results</h2>
            
            {error && (
              <div className="error-message bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
                <strong>Error:</strong> {error}
              </div>
            )}

            {!analysis && !error && (
              <div className="placeholder text-center text-gray-500 py-12">
                <div className="text-4xl mb-4">📊</div>
                <p>Enter a question and click "Analyze for Diagrams" to see results</p>
              </div>
            )}

            {analysis && (
              <div className="analysis-results">
                {console.log('🔍 Rendering analysis:', analysis)}
                {/* Analysis Summary */}
                <div className="summary mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-medium">Needs Diagram:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      analysis.needs_diagram 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {console.log('🔍 Diagram decision:', analysis.needs_diagram, analysis.needs_diagram ? 'Yes' : 'No')}
                      {analysis.needs_diagram ? 'Yes' : 'No'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-medium">Confidence:</span>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${analysis.confidence * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">{Math.round(analysis.confidence * 100)}%</span>
                    </div>
                  </div>

                  {analysis.diagram_type && (
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-medium">Diagram Type:</span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {analysis.diagram_type.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Reasoning */}
                <div className="reasoning mb-6">
                  <h3 className="text-lg font-medium mb-2">AI Reasoning:</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded italic">
                    "{analysis.reasoning}"
                  </p>
                </div>

                {/* Extracted Values */}
                {analysis.extracted_values && Object.keys(analysis.extracted_values).length > 0 && (
                  <div className="extracted-values mb-6">
                    <h3 className="text-lg font-medium mb-2">Extracted Values:</h3>
                    <div className="bg-gray-50 p-3 rounded">
                      <pre className="text-sm text-gray-700">
                        {JSON.stringify(analysis.extracted_values, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Generated Diagram */}
                {analysis.needs_diagram && analysis.diagram_svg && (
                  <div className="generated-diagram">
                    <h3 className="text-lg font-medium mb-2">Generated Diagram:</h3>
                    <DiagramViewer
                      diagramSvg={analysis.diagram_svg}
                      diagramType={analysis.diagram_type}
                      diagramData={analysis.diagram_data}
                      className="mb-4"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Debug Info */}
      {debugInfo && (
        <div className="debug-info mt-8 p-4 bg-yellow-100 rounded-lg border border-yellow-300">
          <h3 className="font-bold text-yellow-800 mb-2">🐛 Debug Information</h3>
          <pre className="text-xs text-yellow-800 overflow-auto">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default DiagramAnalysisTool;